import PestPillarLayout from "../PestPillarLayout";
import { useCommunes } from "../../lib/useCommunes";

const PROBLEMS = {
  frelons: { title: "Frelons et guêpes", desc: (c) => `Nids de frelons asiatiques dans les arbres, de guêpes sous les toitures et dans les abris de jardin à ${c} : destruction sécurisée avec un équipement adapté.` },
  rongeurs: { title: "Rats et souris", desc: (c) => `Caves, granges, combles et poulaillers à ${c} : diagnostic, piégeage sécurisé et rebouchage des points d'entrée.` },
  chenilles: { title: "Chenilles processionnaires", desc: () => "Pins et chênes des jardins : échenillage en automne-hiver et pose d'éco-pièges avant les processions." },
  punaises: { title: "Punaises de lit", desc: () => "Maisons, gîtes et locations saisonnières : traitement discret en plusieurs passages, avec contrôle de l'efficacité." },
  cafards: { title: "Cafards et blattes", desc: () => "Cuisines, salles de bain, commerces alimentaires : gel appâts et pulvérisation ciblée de produits homologués." },
  xylophages: { title: "Vrillettes et insectes du bois", desc: () => "Charpentes, poutres et meubles anciens : diagnostic de l'attaque et traitement curatif si nécessaire." },
};

const METHOD = [
  { title: "Premier contact", desc: "Appel, WhatsApp ou formulaire : vous décrivez la situation, une photo nous aide souvent à préparer l'intervention." },
  { title: "Diagnostic et devis", desc: "Identification du nuisible, évaluation de l'infestation et devis gratuit, clair et sans engagement." },
  { title: "Intervention", desc: "Traitement adapté par un technicien certifié Certibiocide, avec des produits homologués et des méthodes ciblées." },
  { title: "Suivi et conseils", desc: "Contrôle de l'efficacité si nécessaire et conseils concrets pour éviter le retour des nuisibles." },
];

const RELATED = {
  frelons: [
    { title: "Que faire d'un nid de frelons asiatiques vide en hiver ?", slug: "nid-frelons-asiatiques-vide-hiver-que-faire" },
    { title: "Comment reconnaître un nid de frelons asiatiques", slug: "reconnaitre-nid-frelons-asiatiques-creuse" },
  ],
  rongeurs: [
    { title: "Rats et souris à l'automne : signes d'infestation et prévention", slug: "rats-souris-automne-signes-infestation-prevention" },
    { title: "Dératisation en Creuse : quand faire appel à un professionnel ?", slug: "deratisation-creuse-quand-faire-appel-professionnel" },
  ],
  chenilles: [
    { title: "Chenilles processionnaires : quand et comment s'en débarrasser en Creuse", slug: "chenilles-processionnaires-quand-comment-s-en-debarrasser-creuse" },
    { title: "Chenilles processionnaires : danger et traitement en Creuse", slug: "chenilles-processionnaires-danger-traitement" },
  ],
  punaises: [
    { title: "Punaises de lit : reconnaître, traiter et éviter la récidive", slug: "punaises-de-lit-reconnaitre-traiter-eviter" },
    { title: "Que faire d'un nid de frelons asiatiques vide en hiver ?", slug: "nid-frelons-asiatiques-vide-hiver-que-faire" },
  ],
  xylophages: [
    { title: "Rats et souris à l'automne : signes d'infestation et prévention", slug: "rats-souris-automne-signes-infestation-prevention" },
    { title: "Comment reconnaître un nid de frelons asiatiques", slug: "reconnaitre-nid-frelons-asiatiques-creuse" },
  ],
};

const buildFaq = (city) => [
  city.faq,
  {
    q: `Intervenez-vous rapidement à ${city.name} ?`,
    a: `${city.name} fait partie de notre secteur habituel autour de ${city.hub}. Nous vous recontactons au plus vite pour fixer un rendez-vous ; pour un nid de frelons actif près d'une habitation, nous faisons le maximum pour intervenir rapidement.`,
  },
  {
    q: "Le devis est-il gratuit ?",
    a: "Oui, le devis est gratuit et sans engagement. Une photo envoyée par WhatsApp permet souvent de vous donner une première estimation avant de se déplacer.",
  },
  {
    q: "Les produits utilisés sont-ils sûrs pour ma famille et mes animaux ?",
    a: "Nous utilisons uniquement des produits homologués, appliqués par un technicien certifié Certibiocide, et privilégions les méthodes mécaniques et ciblées lorsque des enfants ou des animaux sont présents.",
  },
];

export default function PestCityPage({ city }) {
  const communes = useCommunes({ includeHidden: true });
  const customIntro = communes.find((c) => c.key === city.slug)?.text;
  const longTitle = `Anti-nuisibles ${city.name} (23) — Frelons, rats | K'REUSE`;
  return (
    <PestPillarLayout
      slug={city.slug}
      seoTitle={longTitle.length <= 60 ? longTitle : `Anti-nuisibles ${city.name} (23) | K'REUSE`}
      seoDescription={`Frelons, guêpes, rats, souris, chenilles, punaises de lit à ${city.name} (23) : technicien certifié Certibiocide, devis gratuit.`}
      breadcrumbLabel={`Anti-nuisibles ${city.name}`}
      heroBadge={`${city.name} · Creuse (23) · Certibiocide`}
      heroTitleHtml={`Anti-nuisibles à ${city.name} <span class="font-medium text-[#1B5E20]">(Creuse)</span>`}
      heroSubtitle={city.subtitle}
      hero_h2={`Lutte contre les nuisibles à ${city.name}`}
      introParagraphs={[
        customIntro || city.intro,
        `K'REUSE EXTÉRIEUR intervient à ${city.name} avec un technicien certifié Certibiocide, l'agrément d'État obligatoire pour l'usage professionnel des produits biocides. Chaque intervention commence par un diagnostic pour choisir la méthode la plus adaptée, la plus ciblée possible, avant de vous remettre un devis gratuit.`,
      ]}
      problems={city.focus.map((k) => ({ title: PROBLEMS[k].title, desc: PROBLEMS[k].desc(city.name) }))}
      method={METHOD}
      cities={[...city.nearby, ...(city.hub !== city.name && !city.nearby.includes(city.hub) ? [city.hub] : [])]}
      faq={buildFaq(city)}
      relatedArticles={RELATED[city.focus[0]] || RELATED.frelons}
      ctaTitle={`Un problème de nuisibles à ${city.name} ? Parlons-en.`}
      whatsappText={`Bonjour, je souhaite un devis anti-nuisibles à ${city.name}.`}
      withQuoteForm
    />
  );
}
